import React, { FC, useCallback, useMemo } from "react";
import { Pressable, ViewStyle } from "react-native";
import { ALIGN, COLOR, DISPLAY, FLEX_DIRECTION, View, Image, TEXT_ROLES, POINTER } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import "@lookiero/sty-psp-ui";
import { ButtonIcon, Checkbox, Text } from "@lookiero/sty-psp-ui";
import { ItemStatus, Product } from "../../../../domain/entities/product/Product";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import { DOMAIN, I18nMessages } from "../../i18n/i18n";
import { cdnImageUrl } from "../../shared/util/cdnImageUrl";
import { financial } from "../../shared/util/financial/financial";
import { PRODUCT_STATUS } from "../../shared/util/productStatus";
import { ItemLockSVG } from "../itemLockSVG/ItemLockSVG";
import { Label } from "../label/Label";
import { TouchableWithoutFeedback } from "../touchableWithoutFeedback/TouchableWithoutFeedback";
import { IMAGE_HEIGHT, IMAGE_WIDTH, style as productItemStyle } from "./ProductItem.styles";

interface ProductItemProps {
  readonly crossed?: boolean;
  readonly product: Product;
  readonly selected?: boolean;
  readonly showLastUnitsLabel?: boolean;
  readonly showLastUnitsStock?: boolean;
  readonly showNotAvailableLabel?: boolean;
  readonly onAddToWishlist?: (product: Product) => void;
  readonly onDeleteFromWishlist?: (product: Product) => void;
  readonly onPressCard?: (product: Product, selected: boolean) => void;
  readonly onPressDeleteIcon?: (product: Product) => void;
  readonly onPressImage?: (product: Product) => void;
  readonly onSelect?: (product: Product, selected: boolean) => void;
}

// TODO
// beware of crossed prop, because it's name makes to think that is the one of crossing the product but is not. Other props are involved also
const ProductItem: FC<ProductItemProps> = ({
  crossed: crossedProp,
  product,
  selected,
  showLastUnitsLabel,
  showLastUnitsStock,
  showNotAvailableLabel,
  onAddToWishlist,
  onDeleteFromWishlist,
  onPressCard,
  onPressDeleteIcon,
  onPressImage,
  onSelect,
}) => {
  const {
    available,
    brand,
    color,
    imageUrl,
    isAvailable,
    itemStatus,
    lastUnits,
    price,
    size,
    title,
    variantId,
    lastUnitsStock,
    wishlisted,
  } = product;

  const colorText = useI18nMessage({ domain: DOMAIN, id: `catalog.color.${color}` });
  const quantityText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.LABEL_LAST_ITEMS_QUANTITY,
    values: { quantity: lastUnitsStock },
  });
  const labelLastUnitsText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.LABEL_LAST_ITEMS,
  });
  const labelOutOfStockText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.LABEL_OUT_OF_STOCK,
  });

  const formatCurrency = useFormatCurrency();

  const style = useMemo(() => productItemStyle(), []);

  const isCardClickable = Boolean(onPressCard);
  const isCrossedItem = [ItemStatus.DISCARDED, ItemStatus.RETURN_DONE].includes(itemStatus);
  const crossed = crossedProp || isCrossedItem;

  const notAvailable = !isAvailable || !available || PRODUCT_STATUS.UNAVAILABLE.includes(itemStatus);

  const missing = showNotAvailableLabel && !crossed && notAvailable;

  const shouldCross = crossed || selected;

  const handleSelected = useCallback(() => {
    onSelect?.(product, !selected);
  }, [onSelect, product, selected]);

  return (
    <TouchableWithoutFeedback
      pointer={isCardClickable ? POINTER.AUTO : POINTER.NONE}
      style={[style.container, { cursor: isCardClickable ? "pointer" : "default" } as ViewStyle]}
      testID="product-list-item"
      onPress={() => {
        if (onPressCard) {
          onPressCard(product, !selected);
        }
      }}
    >
      {onSelect && (
        <Pressable disabled={crossed} style={style.checkbox} testID="check-option" onPress={handleSelected}>
          <Checkbox checked={selected || crossed} />
        </Pressable>
      )}
      <View style={style.productDetail} wide>
        <TouchableWithoutFeedback onPress={() => (onPressImage ? onPressImage(product) : undefined)}>
          <View
            style={[
              style.thumbnailWrapper,
              shouldCross ? style.activeBorder : undefined,
              // TouchableWithoutFeedback does not handle opacity (set by disabled style), so we need to introduce a wrapper to set the image's, lock's and border's opacity
              missing ? style.disabled : undefined,
            ]}
          >
            <Image
              alt={title}
              resizeMode="cover"
              src={cdnImageUrl({ imageUrl, width: IMAGE_WIDTH })}
              style={style.thumbnail}
            />
            {shouldCross ? <ItemLockSVG height={IMAGE_HEIGHT} width={IMAGE_WIDTH} /> : null}
          </View>
        </TouchableWithoutFeedback>
        <View style={style.productInfo}>
          <View style={missing ? undefined : style.productVariantInfo}>
            <Text level={2} numberOfLines={1} style={style.brand} variant="detail">
              {brand}
            </Text>
            <Text level={2} variant="detail">
              {title}
            </Text>
            <Text level={2} style={style.variant} variant="detail">
              {size} / {colorText}
            </Text>
          </View>

          {lastUnits && showLastUnitsLabel && (
            <View style={style.labelWrapper}>
              <Label color={COLOR.BG_WARNING}>{showLastUnitsStock ? quantityText : labelLastUnitsText}</Label>
            </View>
          )}
          {missing && (
            <View style={style.labelWrapper}>
              <Label color={COLOR.BG_WARNING} role={TEXT_ROLES.p}>
                {labelOutOfStockText}
              </Label>
            </View>
          )}

          <View
            alignItems={ALIGN.END}
            display={DISPLAY.FLEX}
            flexDirection={FLEX_DIRECTION.ROW}
            justifyContent={ALIGN.BETWEEN}
          >
            {!product.isProductWithDiscount ? (
              <Text level={2} style={missing ? style.priceMissing : style.price} variant="detail">
                {formatCurrency({ amount: financial(price.amount) })}
              </Text>
            ) : (
              <View style={style.priceWrapper}>
                <Text level={2} style={missing ? style.priceMissing : style.price} variant="detail">
                  {price.discountedPrice && formatCurrency({ amount: financial(price.discountedPrice.amount) })}
                </Text>
                <Text
                  level={2}
                  style={missing ? style.priceStrikethroughMissing : style.priceStrikethrough}
                  variant="detail"
                >
                  {formatCurrency({ amount: financial(product.price.amount) })}
                </Text>
              </View>
            )}
          </View>
        </View>
        <View style={style.buttonsList}>
          <View>
            {onAddToWishlist && onDeleteFromWishlist && (
              <ButtonIcon
                name={wishlisted ? "heart_fill" : "heart"}
                style={{ button: style.buttonItem, icon: [style.icon, wishlisted && style.wishlistedIcon] }}
                testID={`add-to-wishlist-button-${variantId}`}
                onPress={() => (wishlisted ? onDeleteFromWishlist(product) : onAddToWishlist(product))}
              />
            )}
          </View>
          {onPressDeleteIcon && (
            <ButtonIcon
              name="trash"
              style={{ button: style.buttonItem }}
              testID={`remove-button-${variantId}`}
              onPress={() => onPressDeleteIcon(product)}
            />
          )}
        </View>
      </View>
    </TouchableWithoutFeedback>
  );
};

export { ProductItem };
